// src/pages/NewsPage.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getArticles } from "../firebase";

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Load articles from Firebase
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const data = await getArticles();
      setArticles(data);
    } catch (error) {
      console.error("Error loading articles:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = ["All", ...new Set(articles.map(article => article.category))];

  // Filter articles
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="py-20 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center text-blue-900 mb-4">Latest News</h1>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-8">
          Breaking news and updates from Okara, Punjab, Pakistan
        </p>

        {/* Search and Filter */}
        <div className="max-w-6xl mx-auto mb-10">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <input
              type="text"
              placeholder="Search news..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                    selectedCategory === category
                      ? "bg-blue-900 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article) => (
              <article key={article.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition duration-300">
                <Link to={`/news/${article.slug}`}>
                  <div className="relative h-48 bg-gray-300">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x300/1a237e/ffffff?text=Kohla+News";
                      }}
                    />
                    {article.isBreaking && (
                      <span className="absolute top-2 left-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                        BREAKING
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                        {article.category}
                      </span>
                      <span>• {article.date}</span>
                      <span>• {article.readTime}</span>
                    </div>
                    <h2 className="font-bold text-lg text-blue-900 hover:text-yellow-500 transition line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-gray-600 text-sm mt-2 line-clamp-2">{article.excerpt}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm text-gray-500">By {article.author}</span>
                      <Link
                        to={`/news/${article.slug}`}
                        className="text-blue-900 font-semibold hover:text-yellow-500 transition text-sm"
                      >
                        Read More →
                      </Link>
                    </div>
                  </div>
                </Link>
              </article>
            ))
          ) : (
            <div className="col-span-3 text-center py-12">
              <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsPage;